/**
 * useSessionTimeout Hook
 *
 * Manages session timeout with server-enforced expiration.
 * - Tracks user activity (mouse, keyboard, scroll, touch)
 * - Sends heartbeats to server while user is active
 * - Shows warning modal when approaching timeout
 * - Auto-logout when session expires
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  SESSION_INACTIVITY_TIMEOUT_MS,
  SESSION_HEARTBEAT_INTERVAL_MS,
  type SessionStatus,
  sendHeartbeat,
  createSession,
  getStoredSessionId,
  isSessionExpiredError,
} from '@perfin/shared';
import { useAuthStore } from '@/stores';
import { performLogout, handleSessionExpired } from '@/services';
import { useToast } from '@/components/ui/Toast';

// ============================================================================
// TYPES
// ============================================================================

export interface SessionTimeoutState {
  /** Current session status */
  status: SessionStatus;
  /** Milliseconds remaining until session expires */
  remainingMs: number;
  /** Whether to show the warning modal */
  showWarning: boolean;
  /** Whether the session is being initialized */
  initializing: boolean;
}

export interface SessionTimeoutActions {
  /** Refresh the session (user clicked "Stay Logged In") */
  refreshSession: () => Promise<void>;
  /** Logout now (user clicked "Logout Now") */
  logoutNow: () => Promise<void>;
  /** Dismiss the warning (continues countdown in background) */
  dismissWarning: () => void;
}

// ============================================================================
// HOOK IMPLEMENTATION
// ============================================================================

export function useSessionTimeout(): SessionTimeoutState & SessionTimeoutActions {
  const navigate = useNavigate();
  const toast = useToast();
  const { user, profile } = useAuthStore();

  // State
  const [status, setStatus] = useState<SessionStatus>('active');
  const [remainingMs, setRemainingMs] = useState(SESSION_INACTIVITY_TIMEOUT_MS);
  const [showWarning, setShowWarning] = useState(false);
  const [initializing, setInitializing] = useState(true);

  // Refs for cleanup
  const heartbeatIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const sessionIdRef = useRef<string | null>(null);

  // Track if user is authenticated
  const isAuthenticated = Boolean(user && profile);

  // ============================================================================
  // SESSION INITIALIZATION
  // ============================================================================

  useEffect(() => {
    if (!isAuthenticated) {
      setInitializing(false);
      return;
    }

    let mounted = true;

    async function initSession() {
      try {
        // Check for existing session
        let sessionId = getStoredSessionId();

        if (!sessionId) {
          // Create new session
          const result = await createSession();
          sessionId = result.sessionId;
        }

        if (mounted) {
          sessionIdRef.current = sessionId;
          setInitializing(false);
          setStatus('active');
          setRemainingMs(SESSION_INACTIVITY_TIMEOUT_MS);
        }
      } catch (error) {
        console.error('[SessionTimeout] Failed to initialize session:', error);
        if (mounted) {
          setInitializing(false);
          // If session creation fails, still allow the app to function
          // The heartbeat will fail and eventually trigger logout
        }
      }
    }

    initSession();

    return () => {
      mounted = false;
    };
  }, [isAuthenticated]);

  // ============================================================================
  // ACTIVITY TRACKING
  // ============================================================================

  const handleActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    // If we were in warning state, activity doesn't automatically clear it
    // User must click "Stay Logged In" to refresh the session
  }, []);

  useEffect(() => {
    if (!isAuthenticated || initializing) return;

    // Activity events to track
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

    // Throttle activity tracking to avoid excessive updates
    let throttleTimeout: ReturnType<typeof setTimeout> | null = null;
    const throttledHandler = () => {
      if (throttleTimeout) return;
      throttleTimeout = setTimeout(() => {
        throttleTimeout = null;
        handleActivity();
      }, 1000); // Throttle to once per second
    };

    for (const event of events) {
      window.addEventListener(event, throttledHandler, { passive: true });
    }

    return () => {
      for (const event of events) {
        window.removeEventListener(event, throttledHandler);
      }
      if (throttleTimeout) clearTimeout(throttleTimeout);
    };
  }, [isAuthenticated, initializing, handleActivity]);

  // ============================================================================
  // HEARTBEAT
  // ============================================================================

  useEffect(() => {
    if (!isAuthenticated || initializing || !sessionIdRef.current) return;

    async function doHeartbeat() {
      const sessionId = sessionIdRef.current;
      if (!sessionId) return;

      try {
        const result = await sendHeartbeat(sessionId);

        setStatus(result.status);
        setRemainingMs(result.remainingMs);

        // Show warning if status is warning and not already shown
        if (result.status === 'warning') {
          setShowWarning(true);
        }

        // Handle expired status
        if (result.status === 'expired') {
          await handleExpiredSession();
        }
      } catch (error) {
        console.error('[SessionTimeout] Heartbeat failed:', error);

        if (isSessionExpiredError(error)) {
          await handleExpiredSession();
        }
        // For other errors, we'll retry on next interval
      }
    }

    // Send initial heartbeat
    doHeartbeat();

    // Set up interval
    heartbeatIntervalRef.current = setInterval(doHeartbeat, SESSION_HEARTBEAT_INTERVAL_MS);

    return () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
    };
  }, [isAuthenticated, initializing]);

  // ============================================================================
  // COUNTDOWN TIMER (for warning modal)
  // ============================================================================

  useEffect(() => {
    if (!showWarning || status !== 'warning') {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      return;
    }

    // Update remaining time every second
    countdownIntervalRef.current = setInterval(() => {
      setRemainingMs((prev) => {
        const newRemaining = Math.max(0, prev - 1000);
        if (newRemaining <= 0) {
          // Time's up - trigger logout
          handleExpiredSession();
        }
        return newRemaining;
      });
    }, 1000);

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
  }, [showWarning, status]);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleExpiredSession = useCallback(async () => {
    // Clear intervals
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }

    setShowWarning(false);
    setStatus('expired');

    // Perform logout
    await handleSessionExpired();

    // Show message and redirect
    toast.info('Your session has expired. Please log in again.');
    navigate('/login', { replace: true });
  }, [navigate, toast]);

  const refreshSession = useCallback(async () => {
    const sessionId = sessionIdRef.current;
    if (!sessionId) return;

    try {
      // Send heartbeat to refresh session
      const result = await sendHeartbeat(sessionId);

      setStatus(result.status);
      setRemainingMs(result.remainingMs);
      setShowWarning(false);

      if (result.status === 'active') {
        toast.success('Session refreshed');
      }
    } catch (error) {
      console.error('[SessionTimeout] Failed to refresh session:', error);

      if (isSessionExpiredError(error)) {
        await handleExpiredSession();
      } else {
        toast.error('Failed to refresh session');
      }
    }
  }, [toast, handleExpiredSession]);

  const logoutNow = useCallback(async () => {
    // Clear intervals
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }

    setShowWarning(false);

    await performLogout({ reason: 'user' });
    navigate('/login', { replace: true });
  }, [navigate]);

  const dismissWarning = useCallback(() => {
    setShowWarning(false);
    // Note: countdown continues in background, user may see warning again
  }, []);

  // ============================================================================
  // CLEANUP
  // ============================================================================

  useEffect(() => {
    return () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, []);

  return {
    status,
    remainingMs,
    showWarning,
    initializing,
    refreshSession,
    logoutNow,
    dismissWarning,
  };
}
